import React from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { useRenderPerformance } from "@/components/utils/performance";
import { cloudinaryService } from "@/helpers";
import { CategoryNavigationProps } from "./CategoryNavigation.types";

// Map of category names to emoji icons (fallback when imageUrl is not available)
const CATEGORY_ICONS: Record<string, string> = {
  Пицца: "🍕",
  Роллы: "🍣",
  Шаурма: "🌯",
  Бургеры: "🍔",
  Закуски: "🥨",
  Супы: "🍲",
  Салаты: "🥗",
  Десерты: "🍰",
  Напитки: "🥤",
  Соусы: "🧂",
  // Add more mappings as needed
  Хит: "🔥",
  Новинки: "🆕",
  "Бабл ти": "🍡",
  Сеты: "🍱",
  Горячее: "🍲",
  "Детское меню": "👶",
  Морс: "🍹",
  "Соусы/добавки": "🧂",
  "Салаты/поке": "🥗",
};

/**
 * Category Navigation Component
 */
export const CategoryNavigation: React.FC<CategoryNavigationProps> = React.memo(
  ({ categories, selectedCategoryId, onCategorySelect, loading = false }) => {
    useRenderPerformance("CategoryNavigation");

    const theme = useTheme();

    // Ref for the scroll container to enable auto-scrolling
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

    // Refs for individual category items to calculate scroll position
    const categoryItemRefs = React.useRef<
      Record<string, HTMLDivElement | null>
    >({});

    // Flag to prevent onCategorySelect during auto-scroll
    const autoScrollingRef = React.useRef(false);

    // State for optimized category images
    const [optimizedImages, setOptimizedImages] = React.useState<
      Record<
        string,
        {
          url: string;
          isCloudinary: boolean;
          fallbackUsed: boolean;
        }
      >
    >({});

    const [imageLoadingStates, setImageLoadingStates] = React.useState<
      Record<string, boolean>
    >({});

    // Effect to optimize category images
    React.useEffect(() => {
      const optimizeImages = async () => {
        const imagePromises = categories
          .filter((category) => category.imageUrl)
          .map(async (category) => {
            try {
              setImageLoadingStates((prev) => ({
                ...prev,
                [category.id]: true,
              }));

              const result = await cloudinaryService.smartTransformImage(
                category.imageUrl!,
                {
                  width: 25,
                  height: 25,
                  crop: "fill",
                  gravity: "auto",
                  quality: "auto",
                  format: "auto",
                }
              );

              setOptimizedImages((prev) => ({
                ...prev,
                [category.id]: {
                  url: result.url,
                  isCloudinary: result.isCloudinary,
                  fallbackUsed: result.fallbackUsed,
                },
              }));

              if (result.fallbackUsed) {
                console.info(
                  "CategoryNavigation: Using fallback URL for category:",
                  category.name
                );
              }
            } catch (error) {
              console.error(
                "CategoryNavigation: Image optimization failed for category:",
                category.name,
                error
              );
              // Fallback to original image
              setOptimizedImages((prev) => ({
                ...prev,
                [category.id]: {
                  url: category.imageUrl!,
                  isCloudinary: false,
                  fallbackUsed: true,
                },
              }));
            } finally {
              setImageLoadingStates((prev) => ({
                ...prev,
                [category.id]: false,
              }));
            }
          });

        await Promise.all(imagePromises);
      };

      if (categories.length > 0) {
        optimizeImages();
      }
    }, [categories]);

    // Auto-scroll to active category
    React.useEffect(() => {
      if (!selectedCategoryId || !scrollContainerRef.current) return;

      const activeElement = categoryItemRefs.current[selectedCategoryId];
      if (!activeElement) return;

      // Add debounce to prevent excessive scrolling during rapid updates
      const timeoutId = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container || !activeElement) return;

        const elementRect = activeElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate element position relative to container
        const elementLeft =
          elementRect.left - containerRect.left + container.scrollLeft;
        const containerPadding = 16; // Match the container padding

        // Always scroll to position active category at the very left (after padding)
        const targetScrollLeft = Math.max(0, elementLeft - containerPadding);

        // Only scroll if there's a significant difference (avoid micro-adjustments)
        const currentScrollLeft = container.scrollLeft;
        const scrollDifference = Math.abs(targetScrollLeft - currentScrollLeft);

        if (scrollDifference > 5) {
          // 5px threshold to avoid unnecessary scrolling
          autoScrollingRef.current = true;

          container.scrollTo({
            left: targetScrollLeft,
            behavior: "smooth",
          });

          // Reset auto-scrolling flag after animation completes
          setTimeout(() => {
            autoScrollingRef.current = false;
          }, 300); // Reduced timeout for more responsive feel
        }
      }, 50); // 50ms debounce for auto-scroll

      return () => clearTimeout(timeoutId);
    }, [selectedCategoryId]);

    // Handle category click
    const handleCategoryClick = React.useCallback(
      (category: any) => {
        // Don't trigger onCategorySelect during auto-scroll
        if (autoScrollingRef.current) return;

        onCategorySelect?.(category);
      },
      [onCategorySelect]
    );

    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 0.75, // 6px
            alignItems: "flex-start",
            justifyContent: "flex-start",
            pb: 2, // 16px
            pt: 0,
            pl: 3.125, // 25px
            pr: 3.125, // 25px
            position: "relative",
            width: "100%",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              height: 0.5, // 4px
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "primary.main",
              borderRadius: 0.25, // 2px
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              bgcolor: "divider",
              borderTop: "1px solid",
              borderColor: "divider",
              pointerEvents: "none",
            },
          }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              sx={{
                borderRadius: 1.25, // 10px
                flexShrink: 0,
                height: 4.125, // 33px - Approximate height of category item
                minWidth: 10, // 80px
              }}
            />
          ))}
        </Box>
      );
    }

    return (
      <Box>
        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 0.75, // 6px
            alignItems: "center",
            justifyContent: "flex-start",
            pb: 2,
            pt: 0,
            px: {
              xs: 2,
              md: 3,
            },
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              height: 0.5, // 4px
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "primary.main",
              borderRadius: 0.25, // 2px
            },
          }}
        >
          {categories.map((category) => {
            const optimizedImage = optimizedImages[category.id];
            const isImageLoading = imageLoadingStates[category.id];
            const fallbackIcon = CATEGORY_ICONS[category.name] || "📋";
            const isActive = selectedCategoryId === category.id;

            return (
              <Box
                key={category.id}
                ref={(el: HTMLDivElement | null) => {
                  categoryItemRefs.current[category.id] = el;
                }}
                onClick={() => handleCategoryClick(category)}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  bgcolor: isActive ? "primary.main" : "transparent",
                  borderRadius: 2.5,
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: isActive ? "primary.main" : "divider",
                  pl: 0.5,
                  pr: 1.5,
                  py: 0.5,
                  transition: theme.transitions.create(
                    ["background-color", "border-color", "color"],
                    {
                      duration: theme.transitions.duration.short,
                    }
                  ),
                  "&:hover": {
                    bgcolor: isActive ? "primary.dark" : "action.hover",
                  },
                  flexShrink: 0, // Prevent compression during layout
                }}
              >
                <Box
                  sx={{
                    width: 25,
                    height: 25,
                    mr: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    opacity: isImageLoading ? 0.7 : 1,
                    transition: theme.transitions.create("opacity", {
                      duration: theme.transitions.duration.short,
                    }),
                  }}
                >
                  {optimizedImage?.url ? (
                    <Box
                      component="img"
                      src={optimizedImage.url}
                      alt={category.name}
                      sx={{
                        width: 3.125, // 25px
                        height: 3.125, // 25px
                        objectFit: "cover",
                      }}
                    />
                  ) : category.imageUrl && !isImageLoading ? (
                    <Box
                      component="img"
                      src={category.imageUrl}
                      alt={category.name}
                      sx={{
                        width: 3.125, // 25px
                        height: 3.125, // 25px
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    fallbackIcon
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    color: isActive ? "primary.contrastText" : "inherit",
                    transition: theme.transitions.create("color", {
                      duration: theme.transitions.duration.short,
                    }),
                  }}
                >
                  {category.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }
);

CategoryNavigation.displayName = "CategoryNavigation";
